import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useApplication } from "@/contexts/ApplicationContext";
import { Mail, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { loginWithMagicLink } from "@/services/authService";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/layout/AppLayout";

const EmailConfirmationPage: React.FC = () => {
  const [isResending, setIsResending] = useState(false);
  const [applicationId, setApplicationId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const { application } = useApplication();
  const location = useLocation();
  const { toast } = useToast();

  // Extract applicationId and email from URL query parameters if present
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    // Get applicationId from URL or fall back to context
    const appId = queryParams.get("applicationId");
    if (appId) {
      setApplicationId(appId);
    } else if (application.applicationId) {
      setApplicationId(application.applicationId);
    }

    // Get email from URL or fall back to context
    const emailParam = queryParams.get("email");
    if (emailParam) {
      setEmail(emailParam);
    } else if (application.basicDetails?.email) {
      setEmail(application.basicDetails.email);
    }
  }, [
    location.search,
    application.applicationId,
    application.basicDetails?.email,
  ]);

  const handleResendEmail = async () => {
    // Use email from state (which comes from URL or context)
    const emailToUse = email || application.basicDetails?.email;

    if (!emailToUse) {
      toast({
        title: "Error",
        description: "No email address found. Please go back and try again.",
        variant: "destructive",
      });
      return;
    }

    if (!applicationId) {
      toast({
        title: "Error",
        description: "Application ID not found. Please go back and try again.",
        variant: "destructive",
      });
      return;
    }

    setIsResending(true);
    try {
      // Get the current origin for redirect URL - use auth-callback
      const redirectUrl = `${window.location.origin}/auth-callback`;

      // Use the applicatio
      const result = await loginWithMagicLink(
        emailToUse,
        applicationId,
        redirectUrl
      );

      if (result.success) {
        toast({
          title: "Email Resent",
          description: "We've sent another magic link to your email address.",
          variant: "default",
        });
      } else {
        throw new Error(result.message || "Failed to resend email");
      }
    } catch (error) {
      console.error("Error resending email:", error);
      toast({
        title: "Error",
        description: "Failed to resend email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-lg mx-auto">
          <div className="flex justify-center mb-6">
            <div className="bg-honeycomb/10 p-4 rounded-full">
              <Mail className="h-16 w-16 text-honeycomb" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-jungle mb-4">
            Check Your Email
          </h1>

          <p className="text-gray-600 mb-6">
            We've sent a magic link to{" "}
            <span className="font-semibold">
              {application.basicDetails.email || "your email address"}
            </span>
            . Click the link in the email to continue your application.
          </p>

          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start text-left">
            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="text-blue-800 text-sm">
                <span className="font-semibold">Don't see the email?</span>{" "}
                Check your spam folder or try the following:
              </p>
              <ul className="text-blue-700 text-sm list-disc ml-5 mt-2">
                <li>Make sure your email address is correct</li>
                <li>Check your spam or junk folder</li>
                <li>Wait a few minutes and refresh your inbox</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <Button
              onClick={handleResendEmail}
              className="w-full bg-honeycomb hover:bg-amber-500 text-white"
              disabled={isResending}
            >
              {isResending ? (
                <>
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  Resending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Resend Magic Link
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default EmailConfirmationPage;
